import axios from 'axios';
import { COMMISSION_ENDPOINTS } from '../constants/api';

/**
 * Backend Matching Service
 * Handles communication with the backend matching API
 */
class BackendMatchingService {
  /**
   * Perform bulk matching of HubSpot deals against WellSky records
   * Uses batch processing to handle large datasets
   * @param {Array} hubspotDeals - Array of HubSpot deal objects
   * @param {Array} wellskyRecords - Array of WellSky record objects
   * @param {number} batchSize - Number of HubSpot records to process per batch (default: 250)
   * @param {Function} onProgress - Progress callback function (optional)
   * @returns {Promise} - API response with matching results
   */
  async matchRecords(hubspotDeals, wellskyRecords, batchSize = 250, onProgress = null) {
    try {
      console.log('🚀 Starting batch matching process...');
      console.log(`📊 ${hubspotDeals.length} HubSpot deals, ${wellskyRecords.length} WellSky records`);
      console.log(`📦 Using batch size: ${batchSize}`);
      console.log(`🔢 BATCH DEBUG: Expected total batches: ${Math.ceil(hubspotDeals.length / batchSize)}`);

      // If the dataset is small enough, do single batch
      if (hubspotDeals.length <= batchSize && this._calculatePayloadSize(hubspotDeals, wellskyRecords) < 25000000) {
        console.log('📝 Dataset small enough for single batch');
        const result = await this._processSingleBatch(hubspotDeals, wellskyRecords);
        if (onProgress) onProgress(100, 1, 1);
        return result;
      }

      // Process in batches
      return await this._processBatches(hubspotDeals, wellskyRecords, batchSize, onProgress);
    } catch (error) {
      console.error('❌ Backend matching failed:', error);
      throw error;
    }
  }

  /**
   * Process records in batches to avoid payload size limits
   * @private
   */
  async _processBatches(hubspotDeals, wellskyRecords, batchSize, onProgress = null) {
    const totalBatches = Math.ceil(hubspotDeals.length / batchSize);
    let allMatches = [];
    let allUnmatched = [];
    let combinedSummary = {
      totalHubspotRecords: hubspotDeals.length,
      totalWellskyRecords: wellskyRecords.length,
      totalMatches: 0,
      totalUnmatched: 0,
      processingTime: 0
    };

    console.log(`🔄 BATCH DEBUG: HubSpot records: ${hubspotDeals.length}, Batch size: ${batchSize}, Total batches: ${totalBatches}`);
    console.log(`🔄 Processing ${totalBatches} batches...`);

    // Pre-filter WellSky records to reduce payload size
    // We'll keep all records but can optimize in the future by filtering
    // based on location, date ranges, or other criteria
    const optimizedWellskyRecords = this._optimizeWellskyRecords(wellskyRecords);
    console.log(`📊 Optimized WellSky records: ${optimizedWellskyRecords.length} (from ${wellskyRecords.length})`);

    for (let i = 0; i < totalBatches; i++) {
      const startIndex = i * batchSize;
      const endIndex = Math.min(startIndex + batchSize, hubspotDeals.length);
      const batchDeals = hubspotDeals.slice(startIndex, endIndex);

      console.log(`📦 Processing batch ${i + 1}/${totalBatches} (${batchDeals.length} HubSpot records)`);
      console.log(`📦 BATCH DEBUG: startIndex=${startIndex}, endIndex=${endIndex}, actual batchSize=${batchSize}`);

      // Update progress
      if (onProgress) {
        const progressPercent = Math.round(((i) / totalBatches) * 100);
        onProgress(progressPercent, i + 1, totalBatches);
      }

      try {
        const batchResult = await this._processSingleBatch(batchDeals, optimizedWellskyRecords);
        
        // Combine results
        allMatches = allMatches.concat(batchResult.data.matches || []);
        allUnmatched = allUnmatched.concat(batchResult.data.unmatched || []);
        
        // Update summary
        combinedSummary.totalMatches += batchResult.data.summary?.totalMatches || 0;
        combinedSummary.totalUnmatched += batchResult.data.summary?.totalUnmatched || 0;
        combinedSummary.processingTime += batchResult.data.summary?.processingTime || 0;

        console.log(`✅ Batch ${i + 1} completed: ${batchResult.data.matches?.length || 0} matches, ${batchResult.data.unmatched?.length || 0} unmatched`);
        
        // Add a delay between batches to avoid overwhelming the server and browser
        if (i < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 50)); // Reduced delay for faster processing
        }
        
        // Force garbage collection hint
        if (typeof window !== 'undefined' && window.gc) {
          window.gc();
        }
      } catch (error) {
        console.error(`❌ Batch ${i + 1} failed:`, error);
        console.error('Error details:', {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data
        });
        
        // Continue with other batches but log the error
        // Add failed deals to unmatched with error reason
        batchDeals.forEach(deal => {
          allUnmatched.push({
            hubspotDeal: deal,
            reason: `Batch processing failed: ${error.message} (${error.response?.status || 'Unknown status'})`
          });
        });
        combinedSummary.totalUnmatched += batchDeals.length;
      }
    }

    // Final progress update
    if (onProgress) {
      onProgress(100, totalBatches, totalBatches);
    }

    console.log('✅ All batches completed successfully');
    console.log(`📊 Final results: ${allMatches.length} matches, ${allUnmatched.length} unmatched`);

    return {
      success: true,
      data: {
        matches: allMatches,
        unmatched: allUnmatched,
        summary: combinedSummary
      }
    };
  }

  /**
   * Optimize WellSky records to reduce payload size
   * Currently keeps all records but can be enhanced with filtering logic
   * @private
   */
  _optimizeWellskyRecords(wellskyRecords) {
    // For now, return all records
    // Future optimizations could include:
    // - Filter by date ranges
    // - Filter by geographic regions
    // - Remove records that are already matched
    // - Limit to recent records only
    
    return wellskyRecords;
  }

  /**
   * Process a single batch of records
   * @private
   */
  async _processSingleBatch(hubspotDeals, wellskyRecords) {
    const response = await axios.post(COMMISSION_ENDPOINTS.MATCH_RECORDS, {
      hubspotDeals,
      wellskyRecords
    }, {
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 300000, // 5 minute timeout for large batches
      maxContentLength: 100000000, // 100MB max content length
      maxBodyLength: 100000000 // 100MB max body length
    });

    return response.data;
  }

  /**
   * Estimate payload size to determine if batching is needed
   * @private
   */
  _calculatePayloadSize(hubspotDeals, wellskyRecords) {
    // Rough estimation: JSON.stringify length * 2 for encoding overhead
    const payload = { hubspotDeals, wellskyRecords };
    return JSON.stringify(payload).length * 2;
  }

  /**
   * Get matching statistics from the backend
   * @returns {Promise} - API response with matching statistics
   */
  async getMatchingStats() {
    try {
      const response = await axios.get(COMMISSION_ENDPOINTS.MATCHING_STATS, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get matching stats:', error);
      throw error;
    }
  }

  /**
   * Process matches and return formatted results for the UI
   * @param {Object} matchingResults - Results from backend matching
   * @returns {Object} - Formatted results for display
   */
  processMatchingResults(matchingResults) {
    const { matches, unmatched, summary } = matchingResults.data;

    // Format matches for table display
    const formattedMatches = matches.map(match => {
      const bestMatch = match.bestMatch;
      
      // Safely construct WellSky name from available fields
      const getWellSkyName = (record) => {
        const firstName = record.firstName || record.clientFirstName || '';
        const lastName = record.lastName || record.clientLastName || record.clientName || '';
        
        if (firstName && lastName) {
          return `${firstName} ${lastName}`;
        } else if (firstName) {
          return firstName;
        } else if (lastName) {
          return lastName;
        } else if (record.responsiblePartyName) {
          return record.responsiblePartyName;
        } else {
          return 'Name Not Available';
        }
      };
      
      return {
        hubspotId: match.hubspotDeal.id,
        hubspotName: match.hubspotDeal.name,
        wellskyId: bestMatch.wellskyRecord.id,
        wellskyName: getWellSkyName(bestMatch.wellskyRecord),
        matchScore: bestMatch.matchScore,
        matchedCriteria: bestMatch.matchedCriteria,
        isEligibleForCommission: bestMatch.isEligibleForCommission,
        investigationReasons: bestMatch.investigationReasons,
        matchCount: match.matchCount,
        
        // Original objects for detailed view
        hubspotDeal: match.hubspotDeal,
        wellskyRecord: bestMatch.wellskyRecord,
        allMatches: match.matches
      };
    });

    // Format unmatched records
    const formattedUnmatched = unmatched.map(item => ({
      hubspotId: item.hubspotDeal.id,
      hubspotName: item.hubspotDeal.name,
      reason: item.reason,
      hubspotDeal: item.hubspotDeal
    }));

    return {
      matches: formattedMatches,
      unmatched: formattedUnmatched,
      summary
    };
  }
}

export default new BackendMatchingService();
